/* eslint-disable react/display-name */
/* eslint-disable no-unused-vars */
import { useState, useCallback, useMemo } from 'react';
import { Button, Modal, Input, Tag, notification, Avatar, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  PhoneOutlined,
  CalendarOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { ActionCreateByUrl } from '../../../store/Actions/CommonAction';
import AdvancedTable from '../../AdvancedTable';
import { GetAllOrderApi } from '../../../utils/api';
import moment from 'moment';
import dayjs from 'dayjs';
import queryString from 'query-string';
import { getStatus } from '../../../utils/functions';
import { calculateBilling, formatCurrency } from '../../../utils/billingUtils';
import { PrakurtiCirculerLogo } from '../../imagepath';
import Header from '../../Header';
import Sidebar from '../../Sidebar';
import { convertToCSV, downloadCSV } from '../../../utils/csvUtils';
import { DownloadOutlined } from '@ant-design/icons';
import { DownloadOrderHistoryApi } from '../../../utils/api';

const { TextArea } = Input;

const OrderHistory = () => {
  const dispatch = useDispatch();
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [exportLoading, setExportLoading] = useState(false);



  // 1. Column Definitions
  const columns = useMemo(() => [
    {
      id: 'sr',
      name: '#',
      width: 50,
      cell: (row, index) => (
        <span style={{ color: '#888' }}>{index + 1}</span>
      ),
    },

    {
      id: 'customer',
      name: 'Customer',
      selector: row => row?.customerId?.name,
      minWidth: 200,
      cell: row => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Link
            to={`/order-history/order-history-overview/${row?.id || row?._id}`}
            style={{ fontWeight: 600, color: '#1890ff', fontSize: '14px' }}
          >
            {row?.customerId?.name || row?.additionalInfo?.name || row?.docs?.parentName || '--'}
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#666' }}>
            <PhoneOutlined style={{ fontSize: '11px' }} />
            {row?.additionalInfo?.phoneNo || row?.customerId?.phoneNo || '--'}
          </div>
          {(row?.customerId?.email || row?.additionalInfo?.email) && (
            <div style={{ fontSize: '11px', color: '#999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '180px' }}>
              {row?.customerId?.email || row?.additionalInfo?.email}
            </div>
          )}
        </div>
      ),
    }, {
      id: 'orderInfo',
      name: 'Order Info',
      selector: row => row?.orderUniqueId,
      width: 180,
      cell: row => {
        const tooltipContent = (
          <div style={{ fontSize: '12px', padding: '4px' }}>
            <div style={{ borderBottom: '1px solid rgba(255,255,255,0.2)', marginBottom: '8px', paddingBottom: '4px', fontWeight: 600 }}>
              Transaction Details
            </div>
            {row?.orderUniqueId && (
              <div style={{ marginBottom: '4px' }}>
                <span style={{ color: 'rgba(255,255,255,0.6)' }}>Order ID: </span>
                <span style={{ fontFamily: 'monospace' }}>{row.orderUniqueId}</span>
              </div>
            )}
            {row?.razorpayOrderId && (
              <div style={{ marginBottom: '4px' }}>
                <span style={{ color: 'rgba(255,255,255,0.6)' }}>Razorpay Order: </span>
                <span style={{ fontFamily: 'monospace' }}>{row.razorpayOrderId}</span>
              </div>
            )}
            {row?.razorpayPaymentId && (
              <div style={{ marginBottom: '4px' }}>
                <span style={{ color: 'rgba(255,255,255,0.6)' }}>Razorpay Payment: </span>
                <span style={{ fontFamily: 'monospace' }}>{row.razorpayPaymentId}</span>
              </div>
            )}
            {row?.paymentMode && (
              <div style={{ marginBottom: '4px' }}>
                <span style={{ color: 'rgba(255,255,255,0.6)' }}>Mode: </span>
                <Tag size="small" style={{ fontSize: '10px', lineHeight: '14px', height: '16px' }}>{row.paymentMode.toUpperCase()}</Tag>
              </div>
            )}
          </div>
        );

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Tooltip title={tooltipContent} placement="right" overlayInnerStyle={{ minWidth: '280px' }}>
              <span style={{ fontWeight: 700, color: '#34495e', fontSize: '13px', cursor: 'help', display: 'flex', alignItems: 'center', gap: '4px' }}>
                {row?.orderUniqueId || '--'}
                <InfoCircleOutlined style={{ fontSize: '11px', color: '#1890ff' }} />
              </span>
            </Tooltip>

            {row?.razorpayOrderId && (
              <span style={{ fontSize: '10px', color: '#95a5a6', fontFamily: 'monospace', lineHeight: '1.2' }}>
                RID: {row.razorpayOrderId}
              </span>
            )}
            {row?.razorpayPaymentId && (
              <span style={{ fontSize: '10px', color: '#95a5a6', fontFamily: 'monospace', lineHeight: '1.2' }}>
                PID: {row.razorpayPaymentId}
              </span>
            )}

            <span style={{ fontSize: '11px', color: '#7f8c8d' }}>
              {row?.createdAt ? moment(row.createdAt).format('DD MMM, hh:mm A') : '--'}
            </span>
            {row?.orderType && (
              <Tag style={{ width: 'fit-content', fontSize: '10px', marginTop: '2px', padding: '0 4px' }}>
                {row.orderType.toUpperCase()}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      id: 'bookingDetails',
      name: 'Booking Details',
      selector: row => row?.bookingFor,
      minWidth: 250,
      cell: row => {
        const info = row?.additionalInfo || {};
        const startDate = info?.startDate;
        const endDate = info?.endDate;
        const bookingDate = info?.bookingDate || info?.date;
        const createdAt = row?.createdAt;
        const updatedAt = row?.updatedAt;
        const startTime = info?.startTime;

        // Determine specific items (Room, Service, Treatment)
        let specifics = info?.serviceName;
        if (!specifics && info?.roomDetails?.length > 0) {
          specifics = info.roomDetails.map(r => r.roomTypeName).join(', ');
        }
        if (!specifics && info?.treatmentData?.length > 0) {
          specifics = info.treatmentData.map(t => t.treatmentName).join(', ');
        }
        if (!specifics && info?.serviceData?.length > 0) {
          specifics = info.serviceData.map(s => s.name).join(', ');
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 600, color: '#E67E22', fontSize: '13px' }}>
                {row?.bookingFor || '--'}
              </span>
              {row?.isSimulation && (
                <Tooltip title={`Created By: ${info?.createdByName || row?.createdByName || 'System'}`}>
                  <Avatar src={PrakurtiCirculerLogo} size={16} />
                </Tooltip>
              )}
            </div>

            {/* Date / Time Display */}
            <div style={{ fontSize: '12px', color: '#555', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <CalendarOutlined style={{ fontSize: '11px' }} />
              {startDate && endDate ? (
                <span>{startDate} <span style={{ color: '#aaa' }}>to</span> {endDate}</span>
              ) : (
                <span>{bookingDate || '--'} {startTime && `at ${startTime}`}</span>
              )}
            </div>

            {/* Specifics (Room/Service Name) */}
            {specifics && (
              <div style={{ fontSize: '11px', color: '#7f8c8d', fontStyle: 'italic', lineHeight: '1.2' }}>
                {specifics}
              </div>
            )}

            {/* Created/Updated Timestamps */}
            {row?.bookingFor === "Appointment" && (createdAt || updatedAt) && (
              <div style={{ fontSize: '10px', color: '#888', marginTop: '2px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
                {createdAt && <span>Created: {moment(createdAt).format('DD MMM YYYY, hh:mm A')}</span>}
                {updatedAt && <span>Updated: {moment(updatedAt).format('DD MMM YYYY, hh:mm A')}</span>}
              </div>
            )}

            {/* Simulation Creator Footer */}
            {(info?.createdByName || row?.createdByName) && (
              <div style={{ fontSize: '10px', color: '#aaa', marginTop: '2px' }}>
                By: {info?.createdByName || row?.createdByName}
              </div>
            )}
          </div>
        )
      },
    },
    {
      id: 'financials',
      name: 'Billing',
      selector: row => row?.amount,
      width: 160,
      cell: row => {
        const billing = calculateBilling(row);

        if (!billing) return '--';

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%', padding: '2px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', minHeight: '16px' }}>
              <Tooltip title="Gross Amount before discount">
                <span style={{
                  color: '#94a3b8',
                  textDecoration: billing.discountValue > 0 ? 'line-through' : 'none',
                  fontSize: '11px',
                  fontWeight: 400
                }}>
                  {formatCurrency(billing.total)}
                </span>
              </Tooltip>
              {billing.discountRate > 0 && (
                <span style={{
                  color: '#10b981',
                  fontSize: '10px',
                  fontWeight: 600,
                  backgroundColor: '#ecfdf5',
                  padding: '0 4px',
                  borderRadius: '2px'
                }}>
                  {billing.discountType === 'percentage' ? `${billing.discountRate}% OFF` : `-${formatCurrency(billing.discountRate)}`}
                </span>
              )}
            </div>

            <div style={{
              fontSize: '15px',
              fontWeight: 600,
              color: '#1e293b',
              letterSpacing: '-0.01em',
              lineHeight: '1.2'
            }}>
              {formatCurrency(billing.netAmount)}
            </div>

            <div style={{ marginTop: '2px' }}>
              {!billing.isFullyPaid ? (
                <div style={{
                  color: '#f59e0b',
                  fontSize: '10px',
                  fontWeight: 600,
                  background: '#fffbeb',
                  padding: '1px 6px',
                  borderRadius: '12px',
                  width: 'fit-content',
                  border: '1px solid #fef3c7',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px'
                }}>
                  <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#f59e0b' }} />
                  Pending: {formatCurrency(billing.pending)}
                </div>
              ) : (
                <div style={{
                  color: '#059669',
                  fontSize: '10px',
                  fontWeight: 600,
                  background: '#ecfdf5',
                  padding: '1px 6px',
                  borderRadius: '12px',
                  width: 'fit-content',
                  border: '1px solid #d1fae5',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px'
                }}>
                  <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#059669' }} />
                  Paid Full
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: 'status',
      name: 'Status',
      width: 140,
      cell: row => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {row?.paymentStatus && (
            <Tag color={getStatus(row.paymentStatus)} style={{ margin: 0, textAlign: 'center', fontSize: '11px', width: '100%' }}>
              Pay: {row.paymentStatus}
            </Tag>
          )}
          {row?.orderStatus && (
            <Tag color={getStatus(row.orderStatus)} style={{ margin: 0, textAlign: 'center', fontSize: '11px', width: '100%' }}>
              Ord: {row.orderStatus}
            </Tag>
          )}
        </div>
      )
    },
    {
      id: 'actions',
      name: 'Actions',
      alignment: 'center',
      width: 80,
      cell: row => {
        const isCancelled = row?.paymentStatus === 'Failed' || row?.orderStatus === 'Cancelled';
        return (
          <Button
            type="text"
            size="small"
            danger
            disabled={isCancelled}
            onClick={() => {
              setSelectedOrderId(row?._id || row?.id);
              setCancelReason('');
              setCancelModalOpen(true);
            }}
            style={{ fontWeight: 500, fontSize: '12px' }}
          >
            {isCancelled ? 'Cancelled' : 'Cancel'}
          </Button>
        );
      },
    },
  ], []);

  // Export CSV function
  const handleExportCSV = useCallback(async () => {
    setExportLoading(true);
    try {
      const formatToYYYYMMDD = value => {
        if (!value) return '';
        const d = dayjs(value, 'DD-MM-YYYY');
        return d.isValid() ? d.format('YYYY-MM-DD') : dayjs(value).format('YYYY-MM-DD');
      };

      // Get current filters from URL params
      const currentParams = queryString.parse(window.location.search);

      const queryParams = {
        ...currentParams,
      };

      // Map Date Range Filters to align with Order Report format
      if (currentParams.orderDateRange_start || currentParams.orderDateRange_end) {
        const startStr = formatToYYYYMMDD(currentParams.orderDateRange_start);
        const endStr = formatToYYYYMMDD(currentParams.orderDateRange_end);

        if (startStr && endStr && startStr === endStr) {
          queryParams.paymentStartDate = startStr;
          queryParams.paymentEndDate = endStr;
          queryParams.todayDate = startStr;
        } else {
          if (startStr) queryParams.paymentStartDate = startStr;
          if (endStr) queryParams.paymentEndDate = endStr;
        }

        // Remove the internal flattened keys used by AdvancedTable
        delete queryParams.orderDateRange_start;
        delete queryParams.orderDateRange_end;
      }

      // Remove pagination params for full export
      delete queryParams.page;
      delete queryParams.perPage;

      const queryStr = '?' + queryString.stringify(queryParams);
      const response = await DownloadOrderHistoryApi({ Query: queryStr });
      const rawData = response?.data;

      // Case 1: API already returns CSV string
      if (typeof rawData === 'string' && rawData.trim().length > 0 && (rawData.includes(',') || rawData.includes('\n'))) {
        downloadCSV(rawData, `Order_History_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
        notification.success({
          message: 'Export Successful',
          description: 'CSV file downloaded from server.',
        });
        return;
      }

      // Case 2: API returns JSON data that needs conversion
      const exportData = rawData?.data?.[0]?.docs || rawData?.data || (Array.isArray(rawData) ? rawData : []);

      if (!Array.isArray(exportData) || exportData.length === 0) {
        notification.info({ message: 'No data to export' });
        return;
      }

      // Filter out columns we don't want in CSV (like the Action column)
      const exportColumns = columns.filter(col => col.id !== 'actions' && col.id !== 'sr');

      const csv = convertToCSV(exportData, exportColumns);
      downloadCSV(csv, `Order_History_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);

      notification.success({
        message: 'Export Successful',
        description: `Exported ${exportData.length} records.`,
      });
    } catch (error) {
      console.error('Error exporting CSV:', error);
      notification.error({
        message: 'Export Failed',
        description: 'Failed to download order history. Please try again.',
      });
    } finally {
      setExportLoading(false);
    }
  }, [columns]);

  // 2. Filter Configuration
  const filterConfig = useMemo(() => [
    {
      id: 'parentName',
      label: 'Customer Name',
      type: 'text',
      placeholder: 'Search name...',
    },
    {
      id: 'phoneNo',
      label: 'Phone Number',
      type: 'text',
      placeholder: 'Search phone...',
    },
    {
      id: 'bookingFor',
      label: 'Booking Type',
      type: 'select',
      options: [
        { label: 'Treatment', value: 'Treatment' },
        { label: 'Consultation', value: 'Consultation' },
        { label: 'Detox', value: 'Detox' },
        { label: 'Service', value: 'Service' },
      ],
    },
    {
      id: 'paymentStatus',
      label: 'Payment Status',
      type: 'select',
      options: [
        { label: 'Completed', value: 'Completed' },
        { label: 'Pending', value: 'Pending' },
        { label: 'Failed', value: 'Failed' },
        { label: 'Initialize', value: 'Initialize' },
      ],
    },
    {
      id: 'orderDateRange',
      label: 'Booking Date Filter',
      type: 'dateRange',
      placeholder: 'Select Date Range',
      quickFilters: ['today', 'yesterday', 'last7days', 'last30days'],
    },
    // Removed specific Start/End Date Range filters as requested "remove start-end date filter"
  ], []);

  // 3. Data Fetching
  const fetchData = useCallback(async ({ page, perPage, sortField, sortOrder, search, ...filters }) => {
    // Construct Query String for API
    const queryParams = new URLSearchParams();
    queryParams.append('page', page);
    queryParams.append('limit', perPage);

    // Filters
    if (search) queryParams.append('search', search); // if global search is supported
    if (filters.parentName) queryParams.append('parentName', filters.parentName);
    if (filters.phoneNo) queryParams.append('phoneNo', filters.phoneNo);
    if (filters.bookingFor) queryParams.append('bookingFor', filters.bookingFor);
    if (filters.paymentStatus) queryParams.append('paymentStatus', filters.paymentStatus);

    // Date Range Filter Logic
    // Date Range Filter Logic
    let startStr, endStr;

    // Check for flattened keys from AdvancedTable (e.g. orderDateRange_start, orderDateRange_end)
    if (filters.orderDateRange_start || filters.orderDateRange_end) {
      startStr = filters.orderDateRange_start ? moment(filters.orderDateRange_start).format('YYYY-MM-DD') : '';
      endStr = filters.orderDateRange_end ? moment(filters.orderDateRange_end).format('YYYY-MM-DD') : '';
    } else if (filters.orderDateRange) {
      // Handle legacy formats or direct object passing
      if (typeof filters.orderDateRange === 'string') {
        const parts = filters.orderDateRange.split(',');
        startStr = parts[0];
        endStr = parts[1] || parts[0];
      } else if (typeof filters.orderDateRange === 'object') {
        startStr = filters.orderDateRange.start ? moment(filters.orderDateRange.start).format('YYYY-MM-DD') : '';
        endStr = filters.orderDateRange.end ? moment(filters.orderDateRange.end).format('YYYY-MM-DD') : '';
      }
    }

    if (startStr && endStr && startStr === endStr) {
      // If both dates are same, send only start date
      queryParams.append('todayDate', startStr);
    } else {
      // Otherwise behave as usual
      if (startStr) queryParams.append('orderStartDate', startStr);
      if (endStr) queryParams.append('orderEndDate', endStr);
    }

    try {
      const response = await GetAllOrderApi({ Query: `?${queryParams.toString()}` });
      if (response?.status === 200) {
        return {
          data: response?.data?.data?.[0]?.docs || [],
          total: response?.data?.data?.[0]?.totalDocs || 0,
        };
      }
      return { data: [], total: 0 };
    } catch (error) {
      console.error('Fetch Error:', error);
      return { data: [], total: 0 };
    }
  }, []);

  // 4. Cancel Order Handler
  const handleCancelOrder = () => {
    if (!cancelReason) {
      notification.warning({ message: 'Please enter a reason for cancellation' });
      return;
    }

    setCancelLoading(true);
    dispatch(
      ActionCreateByUrl(
        {
          Query: `admin/order/order_cancel/${selectedOrderId}`,
          obj: { reason: cancelReason },
        },
        response => {
          setCancelLoading(false);
          if (response?.statusCode === 200) {
            notification.success({ message: 'Order Cancelled Successfully' });
            setCancelModalOpen(false);
            // Ideally trigger refresh here, assuming AdvancedTable exposes a ref or simply let the user manual refresh
            // For now, we rely on the state update or success callback
            window.location.reload(); // Simple way to refresh data
          } else {
            notification.error({ message: response?.message || 'Failed to cancel order' });
          }
        }
      )
    );
  };

  return (
    <>
      <Header />
      <Sidebar />
      <>
        <div className="page-wrapper">
          <div className="content">
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontWeight: 700, color: '#2c3e50' }}>Order History</h3>
              <small style={{ color: '#7f8c8d' }}>Manage and view all customer orders</small>
            </div>

            <AdvancedTable
              resource="OrderHistory"
              columns={columns}
              fetchData={fetchData}
              filterConfig={filterConfig}
              enableSearch={false} // We have custom filters
              enableFilters={true}
              enableExport={false}
              enableRefresh={true}
              enableColumnManager={true}
              persistState
              exportFileName="Order_History_Report"
              customActions={
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleExportCSV}
                  loading={exportLoading}
                  style={{ backgroundColor: '#059669', borderColor: '#059669' }}
                >
                  Export CSV
                </Button>
              }
            />

            {/* Cancel Modal */}
            <Modal
              title="Cancel Order"
              open={cancelModalOpen}
              onCancel={() => setCancelModalOpen(false)}
              onOk={handleCancelOrder}
              confirmLoading={cancelLoading}
              okText="Submit Cancellation"
              okButtonProps={{ danger: true }}
            >
              <div style={{ paddingTop: '10px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Reason for Cancellation:</label>
                <TextArea
                  rows={4}
                  placeholder="Enter specific reason..."
                  value={cancelReason}
                  onChange={e => setCancelReason(e.target.value)}
                />
              </div>
            </Modal>
          </div>
        </div>
      </>
    </>
  );
};

export default OrderHistory;
